import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("validation.too_short"))
    .max(30, i18n.t("validation.too_long"))
    .required(i18n.t("validation.required")),
  description: Yup.string()
    .min(2, i18n.t("validation.too_short"))
    .required(i18n.t("validation.required")),
});

const PaymentMethodModal = ({ open, onClose, paymentMethodId }) => {
  const classes = useStyles();
  const initialState = {
    name: "",
    description: "",
    status: true,
  };
  const [paymentMethod, setPaymentMethod] = useState(initialState);

  useEffect(() => {
    const fetchSession = async () => {
      if (!paymentMethodId) return;

      try {
        const { data } = await api.get(`paymentMethod/${paymentMethodId}`);
        setPaymentMethod(data);

      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [paymentMethodId]);

  const handleSavePaymentMethod = async (values) => {
    try {
      if (paymentMethodId) {
        await api.put(`/paymentMethod/${paymentMethodId}`, values);
      } else {
        await api.post("/paymentMethod", values);
      }
      toast.success(i18n.t("global.toasts.saveSuccess"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleClose = () => {
    onClose();
    setPaymentMethod(initialState);
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {paymentMethodId
            ? `${i18n.t("global.buttons.edit")} ${i18n.t("paymentMethod.name")}`
            : `${i18n.t("global.buttons.add")} ${i18n.t("paymentMethod.name")}`
          }
        </DialogTitle>
        <Formik
          initialValues={paymentMethod}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSavePaymentMethod(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ setFieldValue, values, touched, errors, isSubmitting }) => (

            <Form   >
              <DialogContent dividers>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("global.name")}
                    autoFocus
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                  <FormControlLabel
                    control={
                      <Field
                        as={Switch}
                        color="primary"
                        name="isDefault"
                        checked={values.isDefault}
                      />
                    }
                    label={i18n.t("whatsappModal.form.default")}
                  />
                  <FormControlLabel
                    control={
                      <Field
                        as={Switch}
                        color="primary"
                        name="status"
                        checked={values.status}
                      />
                    }
                    label="Status"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("global.description")}
                    multiline
                    minRows={5}
                    fullWidth
                    name="description"
                    error={
                      touched.description && Boolean(errors.description)
                    }
                    helperText={
                      touched.description && errors.description
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("global.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {paymentMethodId
                    ? i18n.t("global.buttons.okEdit")
                    : i18n.t("global.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(PaymentMethodModal);
